(() => {
  if (window.fvLeadTrackingInitialized) return;
  window.fvLeadTrackingInitialized = true;
  const destination = 'G-CCWRE83H8T';
  const prefix = 'fv-contact-v2:';
  const ttl = 86400000;
  const codePattern = /^FV-[A-F0-9]{32}$/;
  const shortCodePattern = /^FV-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{4}-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{4}$/;
  const services = ['general', 'uv', '3d', 'laser', 'embroidery'];
  const safeService = value => services.includes(value) ? value : 'general';
  const selectedService = () => {
    try {
      const params = new URL(location.href).searchParams;
      const service = params.get('fv_service');
      if (services.includes(service)) return service;
      const quoteKey = params.get('fv_quote');
      if (quoteKey && /^fv-quote-[a-f0-9-]{36}$/.test(quoteKey)) {
        const quote = read(quoteKey);
        if (quote && Number.isFinite(quote.createdAt) && Date.now() >= quote.createdAt && Date.now() - quote.createdAt < ttl) return safeService(quote.service);
      }
    } catch {}
    return 'general';
  };
  const forms = new Map();
  const confirmed = new Map();
  const dispatched = new Set();
  let privacyLoading = false;
  let privacyAttempts = 0;
  const consent = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  const consentState = () => {
    try {
      if (!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed) return 'unknown';
      if (consent()) return 'granted';
      return window.Shopify.customerPrivacy.currentVisitorConsent?.()?.analytics === 'no' ? 'denied' : 'unknown';
    } catch { return 'unknown'; }
  };
  const registerUrl = marker => {
    const value = marker?.dataset.fvRegisterEndpoint;
    return value === 'https://forma-viva-shopify.onrender.com/contact-requests' ? value : '';
  };
  const postRegister = async (url, payload) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: url.endsWith('/status'),
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: controller.signal
      });
      if (!response.ok) {
        const error = new Error('Register unavailable');
        error.retryable = response.status === 408 || response.status === 429 || response.status >= 500;
        if (response.status === 400 && typeof response.json === 'function') {
          try { error.invalidReference = (await response.json()).error === 'Invalid request reference'; } catch {}
        }
        throw error;
      }
      const receipt = await response.json();
      if (receipt.saved !== true) {
        const error = new Error('Invalid register receipt');
        error.retryable = false;
        throw error;
      }
      return receipt;
    } finally { clearTimeout(timer); }
  };
  const saveWithRetry = async (url, payload, onRetry) => {
    // Reuse the receipt and capability even when a saved acknowledgement is lost.
    // Customer data stays in this page only, never in browser storage.
    for (let attempt = 0; attempt < 5; attempt++) {
      try { return await postRegister(url, payload); }
      catch (error) {
        if (error.retryable === false || attempt === 4) throw error;
        onRetry();
        await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** attempt));
      }
    }
  };
  const analyticsIdentity = async entry => {
    if (!consent() || typeof window.gtag !== 'function') return null;
    const state = consentState();
    const get = field => new Promise(resolve => {
      let settled = false;
      const finish = value => { if (!settled) { settled = true; clearTimeout(timer); resolve(value); } };
      const timer = setTimeout(() => finish(null), 1200);
      try { window.gtag('get', destination, field, finish); } catch { finish(null); }
    });
    const [clientId, sessionId] = await Promise.all([get('client_id'), get('session_id')]);
    if (!consent() || state !== 'granted' || !/^\d{1,20}\.\d{1,20}$/.test(String(clientId)) ||
      !/^\d{1,16}$/.test(String(sessionId))) return null;
    return { clientId: String(clientId), sessionId: String(sessionId) };
  };
  const registerStatus = entry => {
    const record = entry.record;
    if (!entry.endpoint || !/^[a-f0-9]{64}$/.test(record.registerToken || '')) return;
    const state = consentState();
    const dispatch = record.isTest ? 'not_attempted' : state !== 'granted' ? (state === 'denied' ? 'consent_denied' : 'not_attempted') :
      record.status === 'analytics_sent' ? 'queued' : typeof window.gtag === 'function' ? 'not_attempted' : 'tag_unavailable';
    const signature = state + ':' + dispatch;
    if (entry.registerSignature === signature || entry.registerBusy || (entry.registerAttempts || 0) >= 8) return;
    entry.registerBusy = true;
    entry.registerAttempts = (entry.registerAttempts || 0) + 1;
    (async () => {
      const identity = record.analyticsTransport === 'server' && record.analyticsConsentAtSubmission === 'granted' && !record.isTest && state === 'granted' ? await analyticsIdentity(entry) : null;
      const latestState = consentState();
      const body = { token: record.registerToken, consent: latestState, observedAt: Date.now(),
        dispatch: record.analyticsTransport === 'server' ? (latestState === 'denied' ? 'consent_denied' : 'not_attempted') : dispatch };
      if (identity && latestState === 'granted') body.analytics = identity;
      await postRegister(entry.endpoint + '/' + record.id + '/status', body);
      if (record.analyticsTransport !== 'server' || record.analyticsConsentAtSubmission !== 'granted' || record.isTest || latestState !== 'granted' || identity) entry.registerSignature = signature;
    })().catch(() => {}).finally(() => { entry.registerBusy = false; });
  };
  const read = key => {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); }
    catch { return null; }
  };
  const write = (key, value) => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); return true; }
    catch { return false; }
  };
  const valid = record => Boolean(record && record.version === 2 && codePattern.test(record.id) &&
    codePattern.test(record.requestId) && ['new_request', 'follow_up'].includes(record.kind) &&
    ['pending', 'confirmed', 'analytics_sent'].includes(record.status) &&
    Number.isFinite(record.time) && Date.now() >= record.time && Date.now() - record.time <= ttl &&
    record.submittedAt === new Date(record.time).toISOString() &&
    typeof record.hasAttachments === 'boolean');
  const newCode = () => {
    try { return 'FV-' + crypto.randomUUID().replaceAll('-', '').toUpperCase(); }
    catch {
      try { return 'FV-' + Array.from(crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('').toUpperCase(); }
      catch { return ''; }
    }
  };
  const safeReferrer = () => {
    try { const url = new URL(document.referrer); return url.origin + url.pathname; }
    catch { return ''; }
  };
  const persistStatus = (entry, status) => {
    const current = read(entry.key);
    if (current?.id !== entry.record.id) return;
    write(entry.key, { ...current, status });
  };
  const sendConfirmed = () => {
    if (!consent() || typeof window.gtag !== 'function') return;
    confirmed.forEach(entry => {
      const record = entry.record;
      if (record.isTest || record.analyticsTransport === 'server') return;
      if (record.status === 'analytics_sent' || dispatched.has(record.id)) return;
      if (!valid(record)) return;
      dispatched.add(record.id);
      persistStatus(entry, 'analytics_sent');
      try {
        window.gtag('event', record.kind === 'follow_up' ? 'contact_follow_up' : 'generate_lead', {
          send_to: destination,
          lead_source: 'contact_form',
          form_id: entry.formId,
          fv_submission_id: record.id,
          fv_request_id: record.requestId,
          fv_request_type: record.kind,
          fv_submitted_at: record.submittedAt,
          fv_has_attachments: record.hasAttachments ? 'yes' : 'no',
          fv_tracking_version: '2',
          fv_service: safeService(record.service),
          page_location: location.origin + location.pathname,
          page_referrer: safeReferrer()
        });
        record.status = 'analytics_sent';
      } catch {
        dispatched.delete(record.id);
        persistStatus(entry, 'confirmed');
      }
    });
  };
  const initialize = marker => {
    const form = marker.closest('form');
    if (!form || forms.has(form)) return;
    const field = selector => form.querySelector(selector);
    const phone = field('[name="contact[phone]"]');
    const whatsapp = field('[data-fv-whatsapp-consent]');
    const validateWhatsAppPhone = () => {
      if (!phone?.setCustomValidity) return;
      let number = String(phone.value || '').replace(/[\s().-]/g, '');
      if (number.startsWith('00')) number = '+' + number.slice(2);
      const valid = /^\+[1-9]\d{7,14}$/.test(number) || /^3\d{9}$/.test(number) || /^0\d{5,10}$/.test(number);
      phone.setCustomValidity(whatsapp?.checked && !valid ? (document.documentElement?.lang?.startsWith('it') ?
        'Inserisci un numero WhatsApp valido con prefisso internazionale, per esempio +39.' :
        'Enter a valid WhatsApp number including the country code, for example +39.') : '');
    };
    phone?.addEventListener('input', validateWhatsAppPhone);
    whatsapp?.addEventListener('change', validateWhatsAppPhone);
    validateWhatsAppPhone();
    const formId = marker.dataset.fvLeadForm;
    const key = prefix + formId;
    const previous = read(key);
    const success = field('[data-fv-lead-success]');
    let hash = '';
    try { hash = decodeURIComponent(location.hash.slice(1)); } catch {}
    const matches = !hash || hash === formId;
    if (success && matches && valid(previous)) {
      const receipt = field('[data-fv-contact-receipt]');
      const receiptCode = field('[data-fv-receipt-code]');
      if (receipt && receiptCode) { receiptCode.textContent = shortCodePattern.test(previous.shortCode) ? previous.shortCode : previous.id; receipt.hidden = false; }
      if (previous.status === 'pending') {
        previous.status = 'confirmed';
        write(key, previous);
      }
      confirmed.set(previous.id, { key, formId, record: previous, endpoint: registerUrl(marker) });
    }
    const entry = {
      key, formId, id: newCode(), time: null,
      analyticsTransport: marker.dataset.fvAnalyticsTransport === 'server' ? 'server' : 'browser',
      analyticsConsentAtSubmission: 'unknown', shortCode: '', requestShortCode: '', canonicalRequestId: '',
      isTest: new URL(location.href).searchParams.get('fv_test') === '1',
      endpoint: registerUrl(marker), marker, registerToken: '', registerFingerprint: '', registerBusy: false,
      service: field('[data-fv-service]'),
      uvHelp: field('[data-fv-uv-help]'),
      type: field('[data-fv-request-type]'),
      reference: field('[data-fv-request-reference]'),
      referenceWrap: field('[data-fv-reference-wrap]'),
      idField: field('[data-fv-submission-id]'),
      requestField: field('[data-fv-request-id]'),
      dateField: field('[data-fv-submitted-at]')
    };
    if (entry.reference && valid(previous)) entry.reference.value = shortCodePattern.test(previous.requestShortCode) ? previous.requestShortCode : previous.requestId;
    if (entry.reference) {
      entry.reference.pattern = '(?:[Ff][Vv]-[A-Fa-f0-9]{32}|[Ff][Vv]-[2-9A-HJ-NP-Za-hj-np-z]{4}-[2-9A-HJ-NP-Za-hj-np-z]{4})';
      const help = field('[id="' + formId + '-reference-help"]');
      if (help) help.textContent = document.documentElement?.lang?.toLowerCase().startsWith('it') ?
        'Inserisci il codice ricevuto con la richiesta precedente (es. FV-7K9M-2P4X). Sono validi anche i vecchi codici lunghi. Non inserire dati personali in questo campo.' :
        'Enter the reference from your previous request (e.g. FV-7K9M-2P4X). Older long codes also work. Do not enter personal details here.';
    }
    if (entry.service) entry.service.value = success && matches && valid(previous) ? safeService(previous.service) : selectedService();
    const update = () => {
      const followUp = entry.type?.value === 'follow_up';
      if (entry.referenceWrap) entry.referenceWrap.hidden = !followUp;
      if (entry.reference) entry.reference.disabled = !followUp;
      if (entry.uvHelp) entry.uvHelp.hidden = entry.service?.value !== 'uv';
    };
    entry.type?.addEventListener('change', update);
    entry.service?.addEventListener('change', update);
    update();
    if (entry.idField) entry.idField.value = entry.id;
    forms.set(form, entry);
  };
  const remember = form => {
    const marker = form?.querySelector?.('[data-fv-lead-form]');
    if (!marker) return;
    initialize(marker);
    const entry = forms.get(form);
    if (!entry || !codePattern.test(entry.id) || !form.checkValidity()) return;
    const kind = entry.type?.value === 'follow_up' ? 'follow_up' : 'new_request';
    const reference = String(entry.reference?.value || '').trim().toUpperCase();
    const requestId = kind === 'follow_up' && (codePattern.test(reference) || shortCodePattern.test(reference)) ?
      (reference === entry.requestShortCode && codePattern.test(entry.canonicalRequestId) ? entry.canonicalRequestId : reference) : entry.id;
    if (entry.time === null) entry.time = Date.now();
    const submittedAt = new Date(entry.time).toISOString();
    if (entry.idField) entry.idField.value = entry.shortCode || entry.id;
    if (entry.requestField) entry.requestField.value = entry.requestShortCode || requestId;
    if (entry.dateField) entry.dateField.value = submittedAt;
    const attachments = form.querySelector('fv-contact-attachments');
    const hasAttachments = Boolean(attachments?.querySelector('input[type="file"]')?.files?.length ||
      attachments?.querySelector('input[type="hidden"]')?.value?.trim());
    const record = {
      version: 2, id: entry.id, requestId, kind, time: entry.time, submittedAt,
      service: safeService(entry.service?.value),
      hasAttachments, status: 'pending'
    };
    if (entry.isTest) record.isTest = true;
    if (entry.analyticsTransport === 'server') {
      record.analyticsTransport = 'server';
      record.analyticsConsentAtSubmission = entry.analyticsConsentAtSubmission;
    }
    if (entry.registerToken) record.registerToken = entry.registerToken;
    if (entry.shortCode) record.shortCode = entry.shortCode;
    if (entry.requestShortCode) record.requestShortCode = entry.requestShortCode;
    write(entry.key, record);
    entry.record = record;
    return entry;
  };
  const saveBeforeSubmit = event => {
    const form = event.target;
    const entry = remember(form);
    if (!entry?.endpoint || entry.registerBypass) return;
    if (entry.registerBusy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (!entry.registerToken) {
      try { entry.registerToken = Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join(''); }
      catch { return; }
    }
    entry.record.registerToken = entry.registerToken;
    entry.analyticsConsentAtSubmission = consentState();
    if (entry.analyticsTransport === 'server') entry.record.analyticsConsentAtSubmission = entry.analyticsConsentAtSubmission;
    write(entry.key, entry.record);
    const fieldValue = name => String(form.querySelector('[name="contact[' + name + ']"]')?.value || '');
    const files = form.querySelector('fv-contact-attachments input[type="file"]')?.files || [];
    const payload = {
      id: entry.record.id, requestId: entry.record.requestId, token: entry.registerToken,
      submittedAt: entry.record.submittedAt, kind: entry.record.kind, service: entry.record.service,
      consent: consentState(), isTest: entry.isTest, transport: entry.analyticsTransport, payload: {
        name: fieldValue('name'), email: fieldValue('email'), phone: fieldValue('phone'), message: fieldValue('body'),
        whatsappConsentVersion: form.querySelector('[data-fv-whatsapp-consent]')?.checked ? 'fv-request-v1' : '',
        website: String(form.querySelector('[data-fv-website]')?.value || ''),
        formId: entry.formId, files: Array.from(files, file => file.name),
        attachmentReferences: String(form.querySelector('fv-contact-attachments input[type="hidden"]')?.value || '')
      }
    };
    const fingerprint = JSON.stringify(payload);
    if (entry.registerFingerprint === fingerprint) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    entry.registerBusy = true;
    const status = form.querySelector('[data-fv-register-status]');
    const show = key => { if (status) { status.hidden = false; status.textContent = entry.marker.dataset[key] || ''; } };
    const operational = form.querySelector('[data-fv-register-result]');
    const privacyField = form.querySelector('[data-fv-analytics-consent]');
    if (privacyField) privacyField.value = payload.consent;
    const italian = document.documentElement?.lang?.toLowerCase().startsWith('it');
    const showMessage = message => { if (status) { status.hidden = false; status.textContent = message; } };
    const locked = Array.from(form.querySelectorAll('input:not([type="hidden"]), textarea, select, button'))
      .filter(control => !control.disabled);
    locked.forEach(control => { control.disabled = true; });
    let released = false;
    const unlock = () => { if (!released) { released = true; locked.forEach(control => { control.disabled = false; }); } };
    const progress = (state, message = '') => form.dispatchEvent?.(new CustomEvent('fv:contact-progress', { bubbles: true, detail: { state, message } }));
    progress('saving');
    show('fvRegisterSaving');
    saveWithRetry(entry.endpoint, payload, () => showMessage(italian ?
      'Stiamo riprovando a salvare la richiesta. Attendi senza chiudere questa pagina; può richiedere fino a un minuto.' :
      'We are retrying your request. Keep this page open; saving may take up to a minute.')).then(receipt => {
      if (receipt.id === entry.id && shortCodePattern.test(receipt.shortCode) && codePattern.test(receipt.requestId)) {
        entry.shortCode = receipt.shortCode;
        entry.canonicalRequestId = receipt.requestId;
        entry.requestShortCode = shortCodePattern.test(receipt.requestShortCode) ? receipt.requestShortCode : '';
        entry.record.shortCode = entry.shortCode;
        entry.record.requestId = entry.canonicalRequestId;
        entry.record.requestShortCode = entry.requestShortCode;
        write(entry.key, entry.record);
        if (entry.idField) entry.idField.value = entry.shortCode;
        if (entry.requestField) entry.requestField.value = entry.requestShortCode || entry.canonicalRequestId;
      }
      if (operational) operational.value = 'saved';
      entry.registerFingerprint = fingerprint;
      show('fvRegisterSaved');
      unlock();
      entry.registerBusy = false;
      entry.registerBypass = true;
      try { form.requestSubmit(event.submitter || undefined); }
      finally { entry.registerBypass = false; }
    }).catch(error => {
      if (operational) operational.value = 'unavailable';
      // Never continue to Shopify without a durable record; keep fields for an explicit retry.
      showMessage(error.invalidReference ? (italian ?
        'Il codice della richiesta precedente non è riconosciuto. Controllalo e riprova, oppure seleziona Nuova richiesta di preventivo. Il modulo non è stato inviato.' :
        'The previous request code was not recognized. Check it and retry, or select New quote request. The form was not sent.') : italian ?
        'Non siamo riusciti a salvare la richiesta. Il modulo non è stato inviato. I campi sono ancora compilati: premi Invia per riprovare, oppure contattaci via email o WhatsApp. Non chiudere questa pagina.' :
        'Your request could not be saved and the form was not sent. Your fields are still filled in: press Send to retry, or contact us by email or WhatsApp. Keep this page open.');
      progress('error', status?.textContent || '');
    }).finally(() => {
      unlock();
      entry.registerBusy = false;
    });
  };
  const loadPrivacy = () => {
    if (window.Shopify?.customerPrivacy || !window.Shopify?.loadFeatures || privacyLoading || privacyAttempts >= 6) return;
    privacyLoading = true;
    privacyAttempts += 1;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        privacyLoading = false;
        sendConfirmed();
      });
    } catch { privacyLoading = false; }
  };
  document.querySelectorAll('[data-fv-lead-form]').forEach(initialize);
  document.addEventListener('submit', saveBeforeSubmit, true);
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || !consent() || typeof window.gtag !== 'function') return;
    let url;
    try { url = new URL(link.href); } catch { return; }
    if (!['wa.me', 'api.whatsapp.com', 'web.whatsapp.com'].includes(url.hostname)) return;
    window.gtag('event', 'whatsapp_click', {
      send_to: destination, contact_channel: 'whatsapp',
      page_location: location.origin + location.pathname, page_referrer: safeReferrer()
    });
  });
  const resume = () => { loadPrivacy(); sendConfirmed(); confirmed.forEach(registerStatus); };
  document.addEventListener('visitorConsentCollected', resume);
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('pageshow', resume);
  window.addEventListener('online', resume);
  document.addEventListener('load', resume, true);
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-fv-lead-form]').forEach(initialize);
    resume();
  });
  const started = Date.now();
  const retry = () => {
    resume();
    if (Date.now() - started < 120000 && (confirmed.size || !window.Shopify?.customerPrivacy)) setTimeout(retry, 2000);
  };
  retry();
})();
